import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';
import axios from 'axios';

const VoteModal = ({ proposal, isOpen, onClose }) => {
  const { address, isConnected, signMessageAsync, getEnsName } = useWallet();
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [voting, setVoting] = useState(false);
  const [voteResult, setVoteResult] = useState(null);
  const [votingReason, setVotingReason] = useState('');
  const [voterEnsName, setVoterEnsName] = useState(null);

  useEffect(() => {
    const fetchEnsName = async () => {
      if (address) {
        const ensName = await getEnsName(address);
        setVoterEnsName(ensName);
      }
    };

    fetchEnsName();
  }, [address, getEnsName]);

  const submitVote = async () => {
    if (!isConnected || !selectedChoice || !proposal) return;

    setVoting(true);
    try {
      // Prepare vote message for signing (Snapshot format)
      const voteMessage = {
        space: proposal.space.id,
        proposal: proposal.id,
        choice: selectedChoice,
        metadata: JSON.stringify({}),
        from: address,
        timestamp: Math.floor(Date.now() / 1000),
        reason: votingReason || '',
        app: 'safedash',
      };

      // Create the message to sign (Snapshot specific format)
      const msg = JSON.stringify(voteMessage, null, 2);
      
      // Sign the message
      const signature = await signMessageAsync({ message: msg });

      // Submit to Snapshot - CORRECT ENDPOINT
      const response = await axios.post('https://hub.snapshot.org/api/msg', {
        address: address,
        sig: signature,
        data: voteMessage,
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data) {
        setVoteResult({ 
          success: true, 
          hash: response.data.id || `vote-${Date.now()}`,
          choice: proposal.choices[selectedChoice - 1]
        });
        
        // Refresh proposals after successful vote
        setTimeout(() => {
          window.dispatchEvent(new Event('proposalVoted'));
        }, 2000);
      } else {
        throw new Error('Failed to submit vote');
      }
    } catch (error) {
      console.error('Vote failed:', error);
      setVoteResult({ 
        success: false, 
        error: error.response?.data?.error?.message || 
               error.response?.data?.message || 
               error.message 
      });
    } finally {
      setVoting(false);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedChoice(null);
      setVoteResult(null);
      setVotingReason('');
    }
  }, [isOpen]);

  if (!isOpen || !proposal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-dominant">Cast Your Vote</h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={voting}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2 text-gray-900 line-clamp-2">{proposal.title}</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p>DAO: <span className="font-medium">{proposal.space.name}</span></p>
            <p>Proposal: <code className="bg-gray-100 px-1 rounded">{proposal.id.slice(0, 8)}...</code></p>
            <div className="flex items-center gap-2">
              <span>Your address:</span>
              <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                <code className="text-xs">
                  {voterEnsName || `${address?.slice(0, 6)}...${address?.slice(-4)}`}
                </code>
                {voterEnsName && (
                  <span className="text-green-600 text-xs" title="ENS Verified">
                    ✓
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rest of the VoteModal component remains the same */}
        {!isConnected ? (
          <div className="text-center py-4">
            <i className="fas fa-wallet text-3xl text-gray-400 mb-3"></i>
            <p className="text-gray-600 mb-4">Connect your wallet to vote on this proposal</p>
            <button className="bg-secondary text-white px-6 py-2 rounded-lg hover:bg-dominant transition-colors">
              Connect Wallet
            </button>
          </div>
        ) : voteResult ? (
          <div className={`text-center py-4 ${
            voteResult.success ? 'text-green-600' : 'text-red-600'
          }`}>
            <i className={`fas fa-${voteResult.success ? 'check-circle' : 'exclamation-circle'} text-3xl mb-3`}></i>
            <p className="font-medium text-lg mb-2">
              {voteResult.success ? 'Vote Submitted!' : 'Vote Failed'}
            </p>
            {voteResult.success && (
              <p className="text-sm text-gray-600 mb-3">
                You voted for: <strong>{voteResult.choice}</strong>
              </p>
            )}
            {voteResult.hash && (
              <a
                href={`https://snapshot.org/#/${proposal.space.id}/proposal/${proposal.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-secondary hover:text-dominant text-sm transition-colors"
              >
                <i className="fas fa-external-link-alt"></i>
                View on Snapshot
              </a>
            )}
            {voteResult.error && (
              <p className="text-sm text-red-600 mt-2">{voteResult.error}</p>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Select your vote:
              </label>
              {proposal.choices.map((choice, index) => (
                <label 
                  key={index} 
                  className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-secondary cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    name="voteChoice"
                    value={index + 1}
                    checked={selectedChoice === index + 1}
                    onChange={(e) => setSelectedChoice(parseInt(e.target.value))}
                    className="text-secondary focus:ring-secondary"
                  />
                  <span className="flex-1 font-medium">{choice}</span>
                </label>
              ))}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voting Reason (Optional):
              </label>
              <textarea
                value={votingReason}
                onChange={(e) => setVotingReason(e.target.value)}
                placeholder="Explain why you're voting this way..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                rows="3"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={voting}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:border-gray-400 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitVote}
                disabled={!selectedChoice || voting}
                className="flex-1 bg-secondary text-white py-3 rounded-lg hover:bg-dominant disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {voting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Submitting Vote...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Submit Vote
                  </>
                )}
              </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                <div className="text-xs text-blue-700">
                  <p>Your vote will be signed and submitted to Snapshot.</p>
                  <p>This action requires a signature but no gas fee.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoteModal;